<script setup lang="ts">
import { onMounted, onUnmounted } from 'vue';
import { confirmModal, confirmModalPick, type ConfirmVariant } from '../confirm-modal';

// Minimal-luxury "Ledger" language — same as the job-form documents card:
// monochrome, editorial overline, hairlines, no icon badges or gradient
// bars. The only variant-driven colour is the primary button for a real
// destructive action (danger); everything else stays near-black/grey.
const overlineText: Record<ConfirmVariant, string> = {
  default: 'ยืนยันการทำงาน',
  danger: 'ลบข้อมูล',
  warning: 'ต้องเลือกดำเนินการ',
  info: 'แจ้งให้ทราบ',
};
const confirmBtnClasses: Record<ConfirmVariant, string> = {
  default: 'cm-btn-solid',
  danger: 'cm-btn-solid cm-btn-solid--danger',
  warning: 'cm-btn-solid',
  info: 'cm-btn-solid',
};

function onKeydown(event: KeyboardEvent) {
  if (event.key === 'Escape' && confirmModal.show) {
    event.preventDefault();
    confirmModalPick('cancel');
  }
}

onMounted(() => window.addEventListener('keydown', onKeydown));
onUnmounted(() => window.removeEventListener('keydown', onKeydown));
</script>

<template>
  <div v-if="confirmModal.show" class="modal-overlay-center z-[300]">
    <div class="absolute inset-0 bg-slate-900/40 backdrop-blur-[2px]" @click="confirmModalPick('cancel')"></div>
    <div class="confirm-modal-panel cm-panel relative w-full max-w-md">
      <button
        type="button"
        @click="confirmModalPick('cancel')"
        class="cm-close"
        aria-label="ปิด"
      >
        <i class="ph ph-x"></i>
      </button>

      <span class="cm-overline">{{ overlineText[confirmModal.variant] }}</span>
      <h3 class="cm-title">{{ confirmModal.title }}</h3>
      <p class="cm-message">{{ confirmModal.message }}</p>

      <div class="cm-actions">
        <button
          v-if="confirmModal.cancelText"
          type="button"
          @click="confirmModalPick('cancel')"
          class="cm-btn-ghost"
        >
          {{ confirmModal.cancelText }}
        </button>
        <button
          v-if="confirmModal.extraText"
          type="button"
          @click="confirmModalPick('extra')"
          class="cm-btn-outline"
        >
          {{ confirmModal.extraText }}
        </button>
        <button
          type="button"
          @click="confirmModalPick('confirm')"
          :class="confirmBtnClasses[confirmModal.variant]"
        >
          {{ confirmModal.confirmText }}
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.cm-panel {
  background: #fff;
  border: 1px solid rgb(226 232 240 / 0.9);
  border-radius: 20px;
  padding: 28px 26px 24px;
  box-shadow: 0 24px 60px -20px rgb(15 23 42 / 0.3);
}
.cm-close {
  position: absolute;
  top: 16px;
  right: 16px;
  width: 32px;
  height: 32px;
  border-radius: 999px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #cbd5e1;
  transition: color 0.18s ease, background 0.18s ease;
}
.cm-close:hover {
  color: #0f172a;
  background: #f8fafc;
}
.cm-close i {
  font-size: 15px;
}

.cm-overline {
  display: block;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: #94a3b8;
  margin-bottom: 10px;
}
.cm-title {
  font-size: 19px;
  font-weight: 600;
  color: #0f172a;
  margin: 0 0 8px;
  padding-right: 24px;
  letter-spacing: -0.01em;
}
.cm-message {
  font-size: 13.5px;
  line-height: 1.65;
  color: #64748b;
  margin: 0;
  white-space: pre-line;
}

.cm-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  align-items: center;
  gap: 10px;
  margin-top: 24px;
  padding-top: 18px;
  border-top: 1px solid #f1f5f9;
}

.cm-btn-ghost {
  font-size: 13px;
  font-weight: 500;
  color: #94a3b8;
  padding: 10px 6px;
  transition: color 0.18s ease;
}
.cm-btn-ghost:hover {
  color: #0f172a;
}

.cm-btn-outline {
  font-size: 13px;
  font-weight: 500;
  color: #334155;
  border: 1px solid #e2e8f0;
  border-radius: 999px;
  padding: 10px 18px;
  background: #fff;
  transition: border-color 0.18s ease, background 0.18s ease;
}
.cm-btn-outline:hover {
  background: #f8fafc;
  border-color: #cbd5e1;
}

.cm-btn-solid {
  font-size: 13px;
  font-weight: 500;
  color: #fff;
  background: #2563eb;
  border-radius: 999px;
  padding: 10px 20px;
  transition: transform 0.18s ease, box-shadow 0.18s ease, background 0.18s ease;
}
.cm-btn-solid:hover {
  background: #1d4ed8;
  transform: translateY(-1px);
  box-shadow: 0 12px 26px -10px rgb(37 99 235 / 0.45);
}
.cm-btn-solid:active {
  transform: translateY(0) scale(0.98);
}
.cm-btn-solid--danger {
  background: #7f1d1d;
}
.cm-btn-solid--danger:hover {
  background: #991b1b;
  box-shadow: 0 12px 26px -10px rgb(127 29 29 / 0.5);
}
</style>
